import React, { useMemo } from 'react';
import { View, StyleSheet, Text } from 'react-native';
import { IActivity, toCanonicalActivityType, isActivityType, ActivityType, KeyStage } from '@eduwrite/shared';
import { useAuthStore } from '../store/useAuthStore';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { WorkpadHost } from './WorkpadHost';
import { TracingActivity } from './TracingActivity';
import { MathGridActivity } from './MathGridActivity';
import { LongFormWritingActivity } from './LongFormWritingActivity';
import { SpellingActivity } from './SpellingActivity';
import { SentenceBuildActivity } from './SentenceBuildActivity';
import { SpecialWordsActivity } from './SpecialWordsActivity';
import { ComprehensionActivity } from './ComprehensionActivity';
import { CopyWorkActivity } from './CopyWorkActivity';
import { DictationActivity } from './DictationActivity';
import { CanvasPromptActivity } from './CanvasPromptActivity';
import { InteractiveStoryActivity } from './InteractiveStoryActivity';
import { StoryReaderActivity } from './StoryReaderActivity';
import { FlashcardReviewActivity } from './FlashcardReviewActivity';
import { QuizActivity } from './QuizActivity';
import { PictorialChoiceActivity } from './PictorialChoiceActivity';
import { InkStepAssessmentActivity } from './InkStepAssessmentActivity';
import { MapPlacementActivity } from './MapPlacementActivity';
import { ConcreteMathActivity } from './ConcreteMathActivity';
import { GroupingSortingActivity } from './GroupingSortingActivity';
import { DirectionalSequenceActivity } from './DirectionalSequenceActivity';
import { BlockSequenceActivity } from './BlockSequenceActivity';
import { BarModelActivity } from './BarModelActivity';
import { FractionWallActivity } from './FractionWallActivity';
import { ClauseBuilderActivity } from './ClauseBuilderActivity';
import { TextDetectiveActivity } from './TextDetectiveActivity';
import { BranchingKeyActivity } from './BranchingKeyActivity';
import { TimelineSequenceActivity } from './TimelineSequenceActivity';
import { GridReferenceActivity } from './GridReferenceActivity';

export interface ActivityRunnerProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

/**
 * ActivityRunner — routes an IActivity to the correct student-app component.
 *
 * Routing logic:
 *  1. Normalise any legacy activity-type strings (e.g. 'tracing' →
 *     'letter_tracing') that might still be cached on a tablet post-rename.
 *  2. First check activity.type for types that need a specific component
 *     regardless of mechanic (comprehension, copy_writing, dictation,
 *     common_exception_words, sentence_building, spelling, maths).
 *  3. Then fall back to mechanic-based routing for the remaining types.
 */
export const ActivityRunner: React.FC<ActivityRunnerProps> = ({ activity, onComplete, keyStage: propKeyStage }) => {
    const authKeyStage = useAuthStore((state) => state.keyStage) as KeyStage | undefined;
    const keyStage = propKeyStage || authKeyStage || undefined;
    const flags = useFeatureFlags();

    // Fallback: when the input-mode switcher feature flag is off, strip any
    // teacher-configured allowedInputModes so activities fall back to their
    // legacy single-mode input (canvas, numpad, text, etc.).
    const activityWithFallback = useMemo(() => {
        if (flags.inputModeSwitcherEnabled) return activity;
        return {
            ...activity,
            config: {
                ...activity.config,
                allowedInputModes: undefined,
                defaultInputMode: undefined,
            },
        };
    }, [activity, flags.inputModeSwitcherEnabled]);

    // Legacy payload safety: translate old type strings at render time so
    // cached JSON on tablets doesn't crash after the rename release.
    const canonicalType: ActivityType | string = (() => {
        if (isActivityType(activityWithFallback.type)) return activityWithFallback.type;
        try {
            return toCanonicalActivityType(activityWithFallback.type as unknown as string, activityWithFallback.mechanic);
        } catch {
            return activityWithFallback.type;
        }
    })();

    // ── Type-first routing (types that own their own component) ──
    const renderChoiceOrQuiz = () => {
        if (keyStage === 'EYFS' || activityWithFallback.config?.isPictorial) {
            return <PictorialChoiceActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
        }
        return <QuizActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
    };

    const renderActivity = () => {
        // Universal tracing mechanic or EYFS tracing template check:
        // Reception pupils or any activity explicitly set with tracing mechanic / vector tracing template
        if (
            activityWithFallback.mechanic === 'tracing' ||
            canonicalType === 'letter_tracing' ||
            canonicalType === 'number_tracing' ||
            (keyStage === 'EYFS' && Boolean(activityWithFallback.config?.svgTemplateUrl))
        ) {
            return <TracingActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
        }

        switch (canonicalType) {
            case 'reading_comprehension':
                if (activityWithFallback.mechanic === 'text_detective') {
                    return <TextDetectiveActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                return <ComprehensionActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'copy_writing':
                return <CopyWorkActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'dictation':
                return <DictationActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'common_exception_words':
                return <SpecialWordsActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'sentence_building':
                if (activityWithFallback.mechanic === 'clause_builder') {
                    return <ClauseBuilderActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                return <SentenceBuildActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'spelling':
                if (activityWithFallback.mechanic === 'text_detective') {
                    return <TextDetectiveActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                return <SpellingActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'concrete_maths':
                return <ConcreteMathActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'grouping_sorting':
                return <GroupingSortingActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'directional_sequence':
                return <DirectionalSequenceActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'maths':
                if (activityWithFallback.mechanic === 'bar_model') {
                    return <BarModelActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (activityWithFallback.mechanic === 'fraction_wall') {
                    return <FractionWallActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (
                    activityWithFallback.mechanic === 'concrete_manipulative' ||
                    (keyStage === 'EYFS' && (activityWithFallback.config?.targetCount !== undefined || activityWithFallback.config?.itemType))
                ) {
                    return <ConcreteMathActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                return <MathGridActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            // Foundation subjects (Phase 2 & Phase 3) — canvas, text, map, branching, timeline, blocks, drag_sort, grid_sequence, quiz
            case 'geography_fieldnote':
                if (activityWithFallback.mechanic === 'grid_reference') {
                    return <GridReferenceActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (
                    activityWithFallback.config?.mapType === 'uk_countries' ||
                    activityWithFallback.title?.toLowerCase().includes('map of the uk') ||
                    activityWithFallback.title?.toLowerCase().includes('uk map')
                ) {
                    return <MapPlacementActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (activityWithFallback.mechanic === 'drag_sort') {
                    return <GroupingSortingActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (activityWithFallback.mechanic === 'grid_sequence') {
                    return <DirectionalSequenceActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (activityWithFallback.mechanic === 'choice_tap' || activityWithFallback.config?.questions) {
                    return renderChoiceOrQuiz();
                }
                if (activityWithFallback.mechanic === 'interactive_story') {
                    return <InteractiveStoryActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (activityWithFallback.mechanic === 'branching_key') {
                    return <BranchingKeyActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (activityWithFallback.mechanic === 'timeline_sequence') {
                    return <TimelineSequenceActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (activityWithFallback.mechanic === 'block_sequence') {
                    return <BlockSequenceActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (activityWithFallback.mechanic === 'text') {
                    return <LongFormWritingActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                return <CanvasPromptActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'science_observation':
            case 'history_source':
            case 'art_design_sketch':
            case 'computing_design':
            case 're_reflection':
                if (activityWithFallback.mechanic === 'block_sequence') {
                    return <BlockSequenceActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (activityWithFallback.mechanic === 'branching_key') {
                    return <BranchingKeyActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (activityWithFallback.mechanic === 'timeline_sequence') {
                    return <TimelineSequenceActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (activityWithFallback.mechanic === 'drag_sort') {
                    return <GroupingSortingActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (activityWithFallback.mechanic === 'grid_sequence') {
                    return <DirectionalSequenceActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (activityWithFallback.mechanic === 'choice_tap' || activityWithFallback.config?.questions) {
                    return renderChoiceOrQuiz();
                }
                if (activityWithFallback.mechanic === 'interactive_story') {
                    return <InteractiveStoryActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                if (activityWithFallback.mechanic === 'text') {
                    return <LongFormWritingActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
                }
                return <CanvasPromptActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            // Interactive story (Phase 2) — choice_tap + branching + optional writing
            case 'interactive_story':
                return <InteractiveStoryActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'story_reader':
                return <StoryReaderActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'flashcard_review':
                return <FlashcardReviewActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'quiz':
                return renderChoiceOrQuiz();
            case 'inkstep_assessment':
                return <InkStepAssessmentActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
        }

        // ── Mechanic-based routing for remaining types ──
        const mechanic = activityWithFallback.mechanic || 'canvas';

        switch (mechanic) {
            case 'block_sequence':
                return <BlockSequenceActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'bar_model':
                return <BarModelActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'fraction_wall':
                return <FractionWallActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'clause_builder':
                return <ClauseBuilderActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'text_detective':
                return <TextDetectiveActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'branching_key':
                return <BranchingKeyActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'timeline_sequence':
                return <TimelineSequenceActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'grid_reference':
                return <GridReferenceActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'concrete_manipulative':
                return <ConcreteMathActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'drag_sort':
                return <GroupingSortingActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'grid_sequence':
                return <DirectionalSequenceActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'numpad':
                return <MathGridActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'text':
                return <LongFormWritingActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'word_tap':
                return <SpellingActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'canvas':
            case 'tracing':
                return <TracingActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            case 'choice_tap':
                if (activityWithFallback.config?.questions) {
                    return renderChoiceOrQuiz();
                }
                return <InteractiveStoryActivity activity={activityWithFallback} onComplete={onComplete} keyStage={keyStage} />;
            default:
                return (
                    <View style={styles.errorContainer} accessibilityRole="alert" accessibilityLabel={`Unknown activity: ${mechanic}`}>
                        <Text style={styles.errorText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Unknown activity mechanic: {mechanic}</Text>
                    </View>
                );
        }
    };

    return (
        <View key={activity.id} style={{ flex: 1, minHeight: 0 }}>
            <WorkpadHost activity={activityWithFallback}>
                {renderActivity()}
            </WorkpadHost>
        </View>
    );
};

const styles = StyleSheet.create({
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#FEF2F2',
        borderRadius: 12,
        borderWidth: 2,
        borderColor: '#EF4444',
        margin: 16,
    },
    errorText: {
        fontSize: 18,
        color: '#DC2626',
        fontWeight: 'bold',
    },
});
